import { isBefore } from 'date-fns';
import { useMemo } from 'react';
import type { ItemTypeOption } from 'src/lib/api/types/roadmapping';
import type { Item } from '@/lib/api/types/items';
import { isItemEditable } from '@/lib/utils/roadmapping/isItemEditable';
import type { TimelineItemType } from './useTimelineTableColumns';

export const useFilteredTimelineItems = ({
  from,
  to,
  items,
  curLevelItemTypes,
  nextLevelItemTypes,
  showChildItems,
  integrationItemsEditable,
}: {
  from: string | null;
  to: string | null;
  items: Item[];
  curLevelItemTypes: ItemTypeOption[];
  nextLevelItemTypes: ItemTypeOption[];
  showChildItems: boolean;
  integrationItemsEditable: boolean;
}): TimelineItemType[] => {
  return useMemo<TimelineItemType[]>(() => {
    if (!from || !to) {
      return [];
    }
    const enrichedItems = items.map((item) => ({
      ...item,
      isEditable: isItemEditable(item.permission, item.externalKey, integrationItemsEditable),
    }));

    const curLevelItemTypeIds = new Set(curLevelItemTypes.map((t) => t.id));
    const visibleItems = enrichedItems.filter((item) =>
      Boolean(
        item.targetFinishDateTime &&
          item.targetStartDateTime &&
          curLevelItemTypeIds.has(item.itemTypeId),
      ),
    );

    if (!showChildItems) {
      return visibleItems;
    }

    const nextLevelItemTypeIds = new Set(nextLevelItemTypes.map((t) => t.id));
    const childItemsByParentId = enrichedItems.reduce(
      (obj, item) => {
        if (
          !item.parentId ||
          !item.targetStartDateTime ||
          !item.targetFinishDateTime ||
          isBefore(to, item.targetStartDateTime) ||
          isBefore(item.targetFinishDateTime, from) ||
          !nextLevelItemTypeIds.has(item.itemTypeId)
        ) {
          return obj;
        }

        if (!obj[item.parentId]) {
          obj[item.parentId] = [];
        }
        obj[item.parentId]?.push(item);
        return obj;
      },
      {} as Record<number, TimelineItemType[]>,
    );

    return visibleItems.map((item) => ({
      ...item,
      children: childItemsByParentId[item.id] ?? [],
    }));
  }, [
    from,
    to,
    items,
    curLevelItemTypes,
    nextLevelItemTypes,
    showChildItems,
    integrationItemsEditable,
  ]);
};
